/**
 * Footer newsletter (HubSpot "[DA] Footer Subscription form").
 *
 * HubSpot renders the form, so two details of the field it replaces are
 * applied here once the form appears (and again on every HubSpot re-render):
 * - A placeholder taken from the field's label (without the "*"), falling
 *   back to "Email"; the label stays for screen readers but is hidden
 *   visually (at-footer-newsletter.css).
 * - Keyboard order: email → Submit → everything else in the form (field
 *   errors, consent links, alerts) → the rest of the page. Submit sits on the
 *   email row, but HubSpot puts the consent copy before it in the DOM. The
 *   DOM is HubSpot's (React), so the order is set on Tab instead of by
 *   moving nodes.
 */
(() => {
  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([type="hidden"]):not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    'summary',
    '[tabindex]',
  ]
    .map((selector) => `${selector}:not([tabindex="-1"])`)
    .join(', ');

  /** @param {Element} el */
  const isTabStop = (el) => !el.closest('[inert]') && el.checkVisibility?.({ visibilityProperty: true }) !== false;

  /** @type {Set<HTMLElement>} */
  const roots = new Set();

  /** @param {HTMLElement} root */
  function setPlaceholder(root) {
    const input = root.querySelector('.hsfc-TextInput');
    if (!input || input.placeholder) return;
    const label = input.labels?.[0]?.textContent.replace('*', '').trim();
    input.placeholder = label || 'Email';
  }

  /** Email, Submit, then every other tab stop in the form, in DOM order. */
  function order(root) {
    const input = root.querySelector('.hsfc-TextInput');
    const button = root.querySelector('.hsfc-Button');
    if (!input || !button) return [];
    const rest = [...root.querySelectorAll(FOCUSABLE)].filter((el) => el !== input && el !== button && isTabStop(el));
    return [input, button, ...rest];
  }

  /** First tab stop after the form in document order. */
  function nextOutside(root) {
    return [...document.querySelectorAll(FOCUSABLE)].find(
      (el) => !root.contains(el) && root.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING && isTabStop(el)
    );
  }

  /** @param {KeyboardEvent} event */
  function onKeydown(event) {
    if (event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) return;
    const active = document.activeElement;
    if (!active) return;

    for (const root of roots) {
      if (!root.isConnected) continue;
      const stops = order(root);
      if (!stops.length) continue;

      const index = stops.indexOf(/** @type {HTMLElement} */ (active));
      /** @type {Element | null | undefined} */
      let target = null;

      if (index !== -1) {
        if (!event.shiftKey) {
          target = index < stops.length - 1 ? stops[index + 1] : nextOutside(root);
          if (!target) {
            // Nothing focusable after the form: let the browser leave it,
            // with the form's own stops taken out of the way for this Tab.
            stops.forEach((el) => (el.tabIndex = -1));
            setTimeout(() => stops.forEach((el) => el.removeAttribute('tabindex')));
            return;
          }
        } else if (index > 0) {
          target = stops[index - 1];
        }
      } else if (
        event.shiftKey &&
        root.compareDocumentPosition(active) & Node.DOCUMENT_POSITION_FOLLOWING &&
        active === nextOutside(root)
      ) {
        target = stops[stops.length - 1];
      }

      if (target instanceof HTMLElement) {
        // Only take over the Tab when focus really moved; otherwise the
        // browser's own Tab runs, so a non-focusable target can never trap.
        target.focus();
        if (document.activeElement === target) event.preventDefault();
        return;
      }
    }
  }

  /** @param {ParentNode} scope */
  function init(scope) {
    scope.querySelectorAll('[data-at-footer-newsletter]').forEach((root) => {
      if (!(root instanceof HTMLElement) || roots.has(root)) return;
      roots.add(root);
      new MutationObserver(() => setPlaceholder(root)).observe(root, { childList: true, subtree: true });
      setPlaceholder(root);
    });
  }

  if (!window.__atFooterNewsletter) {
    window.__atFooterNewsletter = true;
    document.addEventListener('keydown', onKeydown, true);
    // Theme editor: a re-rendered footer brings a new root.
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target instanceof Element) init(event.target);
    });
  }

  init(document);
})();
